import {
    concat,
    findIndex,
    last,
    without,
    isNil,
    find,
    isString,
    keyBy,
    uniqBy,
    isEmpty
} from 'lodash/fp';

import { produce } from 'immer';

import { GROUP_NODE } from 'app/lib/constants';
import { createScheduledShareIds, toStringFqn } from 'app/lib/utils';

import { LOAD_PROJECT } from 'app/store/projects/types.js';
import { SIGN_OUT } from 'app/store/users/types.js';
import { GET_VIEW } from 'app/store/view/types.js';
import { GET_SHARED_OUTPUTS_FOR_PROJECT, DELETE_SCHEDULE } from 'app/store/shares/types.js';

import {
    CLOSE_DELETE_ENTITY_DIALOG,
    DELETE_VIEW,
    GET_COLUMN_PROPERTIES,
    OPEN_DELETE_ENTITY_DIALOG,
    DELETE_UNUSED_DATASETS
} from 'app/store/entities/types.js';

import {
    CREATE_NODE_GROUP,
    DELETE_NODE_GROUP,
    RENAME_NODE_GROUP,
    UPDATE_NODE_GROUP_DESCRIPTION
} from 'app/store/flow/types.js';

import { COMBINE_TYPE } from 'app/store/__generated__/types';

import {
    UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED,
    UPDATE_SPACE_DESCRIPTION_SUCCESS_ASSET_DESCRIPTION_UPDATED,
    ADD_TAGS_SUCCESS_TAGS_ADDED,
    REMOVE_TAGS_SUCCESS_TAGS_REMOVED,
    UPDATE_VIEW_NAME_SUCCESS_VIEW_NAME_UPDATED,
    UPDATE_VIEW_DESCRIPTION_SUCCESS_ASSET_DESCRIPTION_UPDATED,
    DELETE_PROJECT_SUCCESS_PROJECT_DELETED,
    UPDATE_ENTITY_DESCRIPTION_SUCCESS_ENTITY_DESCRIPTION_UPDATED,
    DELETE_TRANSFORMATION_SUCCESS_TRANSFORMATION_DELETED,
    SET_ASSET_PROPERTY_VALUE_SUCCESS_ASSET_PROPERTY_VALUE_SET,
    DELETE_ASSET_PROPERTY_VALUE_SUCCESS_ASSET_PROPERTY_VALUE_DELETED,
    GET_SNOWFLAKE_TABLE_ACCESS_SUCCESS_DEPLOYMENT_DETAILS
} from 'app/store/__generated__/events';
import { VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE } from 'app/store/access/types.js';

import { UPDATE_ENTITY_DESCRIPTION } from './types.js';

function ensureEntityExists(draft, entityId) {
    if (!draft.byEntityId.hasOwnProperty(entityId)) {
        draft.byEntityId[entityId] = {};
    }
}

function storeEntity(draft, rawEntity) {
    const { ...entity } = rawEntity;
    let columns;

    ensureEntityExists(draft, entity.entityId);

    if ('payload' in entity) {
        entity._type = entity.payload._type;
    }

    if ('schemasByUid' in entity && 'operationUids' in entity) {
        // For Operation Stacks, the overall schema for the view is the
        // schema for the most-recent operation
        const lastOperationId = last(entity.operationUids);
        columns = entity.schemasByUid[lastOperationId].columns;
    } else if ('schema' in entity) {
        // For Datasources, the schema is singular and attached at the
        // top level of the response
        columns = entity.schema?.columns;
        delete entity.schema;
    }

    // Make sure the sources order matches the input datasets list
    if (entity.payload?._type === COMBINE_TYPE) {
        const sourcesById = keyBy('uid', entity.sources);
        const uniqueInputs = uniqBy('viewUid', entity.payload.inputDatasets);
        entity.sources = uniqueInputs.map(({ viewUid }) => sourcesById[viewUid]);
    }

    if (entity.spaceRef) {
        draft.bySlug[`${entity.spaceRef.slug}/${entity.slug}`] = entity.entityId;
    }

    if (columns) {
        entity.columnIds = [];
        for (const column of columns) {
            const columnUuid = toStringFqn(column.columnUuid);
            entity.columnIds.push(columnUuid);

            // merge or initialize properties
            const properties = draft.byEntityId[columnUuid]
                ? draft.byEntityId[columnUuid].properties
                : [];

            draft.byEntityId[columnUuid] = {
                ...column,
                properties,
                description: column?.description ?? draft.byEntityId[columnUuid]?.description
            };
        }
    }
    if (entity.usedBy) {
        entity.usedBy = entity.usedBy?.map(({ spaceSlug, spaceName, viewUid }) => ({
            projectSlug: spaceSlug,
            name: spaceName,
            viewUid
        }));
    }

    Object.assign(draft.byEntityId[entity.entityId], entity);
}

function storeDataspaceEntity(draft, dataspace) {
    ensureEntityExists(draft, dataspace.entityId);
    Object.assign(draft.byEntityId[dataspace.entityId], dataspace);

    const dataspaceSlug = dataspace.slug || dataspace.spaceSlug;

    draft.bySlug[dataspaceSlug] = dataspace.entityId;
}

function storeViewEntity(draft, view) {
    storeEntity(draft, view);

    const dataspace = draft.byEntityId[view.spaceRef.uid];

    if (dataspace) {
        draft.bySlug[`${dataspace.slug}/${view.uid}`] = view.entityId;
    }
}

function storeSharedOutputEntity(draft, share) {
    const { name, transformationId, type } = share;
    const [spaceUid, parentUid] = transformationId.split('.');
    const { uid, entityId } = createScheduledShareIds(share);
    const transformation = draft.byEntityId[transformationId] ?? {};
    const slug = uid;

    draft.byEntityId[entityId] = {
        _type: type,
        uid,
        spaceUid,
        parentUid,
        transformationId,
        entityId,
        slug,
        name,
        datasetName: transformation.name,
        projectSlug: transformation.spaceSlug
    };

    const spaceSlug = draft.byEntityId[spaceUid]?.slug;

    if (spaceSlug) {
        draft.bySlug[`${spaceSlug}/${slug}`] = entityId;
    }
}

function storeGroupEntity(draft, spaceUid, group) {
    const { id, name, children, description } = group;
    const entityId = `${spaceUid}.${id}`;
    const slug = id;

    draft.byEntityId[entityId] = {
        _type: GROUP_NODE,
        spaceUid,
        uid: id,
        entityId,
        name,
        slug,
        children,
        description
    };

    const projectSlug = draft.byEntityId[spaceUid]?.slug;

    if (projectSlug) {
        draft.bySlug[`${projectSlug}/${slug}`] = entityId;
    }
}

function storeColumnProperty(draft, property) {
    const { entityId, definitionUid, payload } = property;
    const columnId = toStringFqn(entityId);

    let entity;
    if (columnId in draft.byEntityId) {
        entity = draft.byEntityId[columnId];
    }

    if (!isNil(entity)) {
        const index = findIndex({ definitionUid }, entity.properties);

        const definition = {
            definitionUid,
            entityId,
            payload
        };

        if (index === -1) {
            entity.properties.unshift(definition);
        } else {
            entity.properties[index] = definition;
        }
    }
}

function deleteColumnProperty(draft, property) {
    const { entityId, definitionUid } = property;
    const columnId = toStringFqn(entityId);

    let entity;
    if (columnId in draft.byEntityId) {
        entity = draft.byEntityId[columnId];
    }

    if (!isNil(entity)) {
        const index = findIndex({ definitionUid }, entity.properties);

        if (index > -1) {
            entity.properties.splice(index, 1);
        }
    }
}

export const INITIAL_STATE = {
    byEntityId: {},
    bySlug: {},
    isLoading: false,
    entityDescriptionUpdateFailed: false,
    deleteAssetDialog: {
        entityIdForDeletion: null,
        inProgress: false,
        failures: null
    },
    currentEntityId: null
};

export const entitiesReducer = produce((draft, action) => {
    switch (action.type) {
        case OPEN_DELETE_ENTITY_DIALOG: {
            draft.deleteAssetDialog.inProgress = false;
            draft.deleteAssetDialog.entityIdForDeletion = action.params.assetId;
            break;
        }
        case DELETE_VIEW.STARTED:
            draft.deleteAssetDialog.inProgress = true;
            break;
        case DELETE_VIEW.FAILED: {
            const failures = action.payload.failures;
            const failure = isEmpty(failures) ? null : failures[0];
            draft.deleteAssetDialog.inProgress = false;
            draft.deleteAssetDialog.failures = failure;
            break;
        }
        case CLOSE_DELETE_ENTITY_DIALOG: {
            draft.deleteAssetDialog.entityIdForDeletion = null;
            draft.deleteAssetDialog.inProgress = false;
            draft.deleteAssetDialog.failures = null;
            break;
        }

        case DELETE_VIEW.SUCCEEDED:
        case DELETE_UNUSED_DATASETS.PROGRESS: {
            const { viewRef, deletedFromGroup, deletedGroup, tableFqn } = action.payload;
            const { entityId, spaceSlug, spaceUid, viewUid } = viewRef;
            const space = draft.byEntityId[spaceUid];
            const fqnString = toStringFqn(tableFqn);
            delete draft.bySlug[`${spaceSlug}/${viewUid}`];
            delete draft.byEntityId[entityId];

            if (deletedFromGroup) {
                const groupEntityId = `${spaceUid}.${deletedFromGroup}`;
                const group = draft.byEntityId[groupEntityId];
                group.children = without([viewUid], group.children);
            }

            if (deletedGroup) {
                // Remove group entity from project
                space.groupIds = without([deletedGroup], space.groupIds);

                const groupEntityId = `${spaceUid}.${deletedGroup}`;
                const groupSlug = draft.byEntityId[groupEntityId]?.slug;
                delete draft.byEntityId[groupEntityId];
                if (groupSlug) {
                    delete draft.bySlug[`${spaceSlug}/${groupSlug}`];
                }
            }

            if (draft.byEntityId.hasOwnProperty(spaceUid)) {
                draft.byEntityId[spaceUid].viewUids = without([viewUid], space.viewUids);
                draft.byEntityId[spaceUid].viewUuids = without([entityId], space.viewUuids);
                const upstreamUid = space.dependencies.find((d) => d.fromView === viewUid)?.toView;

                draft.byEntityId[spaceUid].dependencies = uniqBy(
                    (d) => `${d.fromView}->${d.toView}`,
                    space.dependencies
                        .filter((d) => d.fromView !== viewUid)
                        .map((d) => (d.toView === viewUid ? { ...d, toView: upstreamUid } : d))
                        .filter((d) => !isNil(d.toView))
                );
            }

            draft.deleteAssetDialog.entityIdForDeletion = null;
            draft.deleteAssetDialog.inProgress = false;
            draft.deleteAssetDialog.failures = null;

            // Remove columns from project
            const assetId = isNil(tableFqn) ? entityId : fqnString;
            const keys = Object.keys(draft.byEntityId);
            const filteredKeys = keys.filter((key) => key.startsWith(`${assetId}.`));

            filteredKeys.forEach((key) => {
                delete draft.byEntityId[key];
            });

            const shareKeys = keys.filter((key) => {
                const transformationId = draft.byEntityId[key]?.transformationId;
                return !isNil(transformationId) && transformationId === entityId;
            });

            shareKeys.forEach((key) => {
                delete draft.bySlug[`${spaceSlug}/${draft.byEntityId[key].slug}`];
                delete draft.byEntityId[key];
            });

            return;
        }
        case DELETE_SCHEDULE.SUCCEEDED: {
            const { viewRef } = action.meta;
            const { projectSlug, slug, entityId } = viewRef;

            delete draft.bySlug[`${projectSlug}/${slug}`];
            delete draft.byEntityId[entityId];

            return;
        }
        case DELETE_PROJECT_SUCCESS_PROJECT_DELETED: {
            const { assetId } = action.payload;

            if (assetId in draft.byEntityId) {
                const { slug } = draft.byEntityId[assetId];
                delete draft.byEntityId[assetId];
                delete draft.bySlug[slug];
            }

            for (const entityId in draft.byEntityId) {
                if (draft.byEntityId[entityId].spaceUid === assetId) {
                    const { slug } = draft.byEntityId[entityId];
                    delete draft.byEntityId[entityId];
                    delete draft.bySlug[slug];
                }
            }
            draft.deleteAssetDialog.entityIdForDeletion = null;

            return;
        }

        case LOAD_PROJECT.SUCCEEDED: {
            const { viewsByUid, groupsByUid, ...project } = action.payload;
            const { uid } = action.payload;

            delete project.ui;
            project.groupIds = Object.keys(groupsByUid);

            storeDataspaceEntity(draft, project);

            for (const viewId in viewsByUid) {
                const view = viewsByUid[viewId];
                storeViewEntity(draft, view);
            }

            for (const groupId in groupsByUid) {
                const group = groupsByUid[groupId];
                storeGroupEntity(draft, uid, group);
            }

            return;
        }

        case GET_SHARED_OUTPUTS_FOR_PROJECT.SUCCEEDED: {
            const { entries } = action.payload;
            for (const entry of entries) {
                storeSharedOutputEntity(draft, entry);
            }
            return;
        }

        case UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED: {
            const { assetId: entityId, name, slug } = action.payload;

            if (entityId in draft.byEntityId) {
                const space = draft.byEntityId[entityId];

                space.name = name;
                space.slug = slug;

                for (const viewEntityId of space.viewUuids) {
                    if (viewEntityId in draft.byEntityId) {
                        const view = draft.byEntityId[viewEntityId];
                        draft.bySlug[`${slug}/${view.slug}`] = viewEntityId;
                    }
                }

                draft.bySlug[slug] = entityId;
            }

            return;
        }

        case UPDATE_VIEW_NAME_SUCCESS_VIEW_NAME_UPDATED: {
            const { assetId: entityId, name } = action.payload;

            if (entityId in draft.byEntityId) {
                draft.byEntityId[entityId].name = name;
            }

            return;
        }

        case UPDATE_ENTITY_DESCRIPTION.STARTED:
            draft.entityDescriptionUpdateFailed = false;
            return;

        case UPDATE_ENTITY_DESCRIPTION.FAILED:
            draft.entityDescriptionUpdateFailed = true;
            return;

        case UPDATE_VIEW_DESCRIPTION_SUCCESS_ASSET_DESCRIPTION_UPDATED:
        case UPDATE_SPACE_DESCRIPTION_SUCCESS_ASSET_DESCRIPTION_UPDATED: {
            const { assetId: entityId, description } = action.payload;

            if (entityId in draft.byEntityId) {
                draft.byEntityId[entityId].description = description;
            }

            return;
        }

        case UPDATE_NODE_GROUP_DESCRIPTION.SUCCEEDED: {
            const { spaceUid, groupUid, newDescription } = action.payload;
            const entityId = `${spaceUid}.${groupUid}`;

            if (entityId in draft.byEntityId) {
                draft.byEntityId[entityId].description = newDescription;
            }

            return;
        }

        case UPDATE_ENTITY_DESCRIPTION_SUCCESS_ENTITY_DESCRIPTION_UPDATED: {
            const { assetId, description } = action.payload;
            const entityId = toStringFqn(assetId);

            if (entityId in draft.byEntityId) {
                draft.byEntityId[entityId].description = description;
            }

            const ids = isString(assetId) && entityId.split('.');

            if (ids?.length === 3) {
                const [spaceId, viewId] = ids;

                const column = find(
                    (c) => c.entityId === entityId,
                    draft.byEntityId[spaceId].schemasByUid[viewId].columns
                );

                if (column) {
                    column.description = description;
                }
            }

            return;
        }

        case ADD_TAGS_SUCCESS_TAGS_ADDED: {
            const { entityId, tags: addedTags } = action.payload;

            if (entityId in draft.byEntityId) {
                draft.byEntityId[entityId].tags = concat(
                    draft.byEntityId[entityId].tags,
                    addedTags
                );
            }

            return;
        }

        case REMOVE_TAGS_SUCCESS_TAGS_REMOVED: {
            const { entityId, tags: removedTags } = action.payload;

            if (entityId in draft.byEntityId) {
                draft.byEntityId[entityId].tags = without(
                    removedTags,
                    draft.byEntityId[entityId].tags
                );
            }

            return;
        }

        case GET_VIEW.SUCCEEDED: {
            storeViewEntity(draft, action.payload.viewDto);

            return;
        }

        case DELETE_TRANSFORMATION_SUCCESS_TRANSFORMATION_DELETED: {
            const {
                operationUid,
                viewRef: { spaceUid, viewUid }
            } = action.payload;
            const entityId = `${spaceUid}.${viewUid}`;

            delete draft.byEntityId[entityId].transformationsByUid[operationUid];
            delete draft.byEntityId[entityId].schemasByUid[operationUid];
            draft.byEntityId[entityId].operationUids = without(
                [operationUid],
                draft.byEntityId[entityId].operationUids
            );

            return;
        }

        case GET_COLUMN_PROPERTIES.STARTED: {
            draft.currentEntityId = toStringFqn(action.meta.entityId);
            break;
        }

        case GET_COLUMN_PROPERTIES.SUCCEEDED: {
            for (const property of action.payload.columnProperties) {
                const { columnUuid: entityId, definitionUid, type, value } = property;
                const columnProperty = {
                    entityId,
                    definitionUid,
                    payload: {
                        type,
                        value
                    }
                };
                storeColumnProperty(draft, columnProperty);
            }
            const descriptionByColumnName = action.payload.descriptionByColumnName;
            for (const name in descriptionByColumnName) {
                const columnId = `${draft.currentEntityId}.${name}`;
                if (columnId in draft.byEntityId) {
                    draft.byEntityId[columnId].description = descriptionByColumnName[name];
                }
            }

            return;
        }

        case SET_ASSET_PROPERTY_VALUE_SUCCESS_ASSET_PROPERTY_VALUE_SET: {
            // all we care about here are column attributes
            // table level attributes are in the tableDetails slice
            storeColumnProperty(draft, action.payload);

            return;
        }

        case DELETE_ASSET_PROPERTY_VALUE_SUCCESS_ASSET_PROPERTY_VALUE_DELETED: {
            // all we care about here are column attributes
            // table level attributes are in the tableDetails slice
            deleteColumnProperty(draft, action.payload);

            return;
        }

        case CREATE_NODE_GROUP.SUCCEEDED: {
            const { group, spaceUid } = action.payload;
            storeGroupEntity(draft, spaceUid, group);
            draft.byEntityId[spaceUid].groupIds.push(group.id);
            return;
        }

        case DELETE_NODE_GROUP.SUCCEEDED: {
            const { spaceUid, groupUid } = action.payload;
            const entityId = `${spaceUid}.${groupUid}`;
            const project = draft.byEntityId[spaceUid];

            // Remove group id from space entity
            project.groupIds = without([groupUid], project.groupIds);

            // Remove the group entity
            delete draft.byEntityId[entityId];

            // Remove the slug mapping
            const slug = `${project.slug}/${groupUid}`;
            delete draft.bySlug[slug];

            return;
        }

        case RENAME_NODE_GROUP.SUCCEEDED: {
            const { spaceUid, groupUid, newName } = action.payload;
            const entityId = `${spaceUid}.${groupUid}`;
            draft.byEntityId[entityId].name = newName;
            return;
        }

        case VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE.SUCCEEDED: {
            for (const entry of action.payload.tables) {
                if (entry._type === GET_SNOWFLAKE_TABLE_ACCESS_SUCCESS_DEPLOYMENT_DETAILS) {
                    const { database, schema, table, deploymentOutOfSync } = entry;
                    const tableFqn = { database, schema, table };

                    const entity = find({ tableFqn }, draft.byEntityId);

                    if (entity) {
                        entity.isOutOfSync = deploymentOutOfSync.length > 0;
                    }
                }
            }
            return;
        }

        case SIGN_OUT:
            return INITIAL_STATE;

        default:
    }
}, INITIAL_STATE);
